import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Heart, Users, Shield, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const teamMembers = [
  {
    name: 'Jason G. Uncapher',
    role: 'Founder',
    bio: 'Visionary leader dedicated to revolutionizing the equestrian boarding industry and creating trusted connections between horse owners and hosts.',
    initials: 'JU',
  },
  {
    name: 'Cindy M. Uncapher',
    role: 'Head of Operations',
    bio: 'Ensuring seamless experiences for every guest and host through operational excellence and attention to detail.',
    initials: 'CU',
  },
  {
    name: 'Whitney W. Uncapher',
    role: 'Community Manager',
    bio: 'Building and nurturing our vibrant community of horse lovers, hosts, and guests across the country.',
    initials: 'WU',
  },
];

const values = [
  {
    icon: Heart,
    title: 'Animal Welfare First',
    description: 'Every decision we make prioritizes the safety and wellbeing of the animals in our community.',
  },
  {
    icon: Shield,
    title: 'Trust & Transparency',
    description: 'We verify every host and maintain open communication to build lasting trust.',
  },
  {
    icon: Users,
    title: 'Community Driven',
    description: 'Our platform is shaped by feedback from the equestrian community we serve.',
  },
  {
    icon: Award,
    title: 'Quality Standards',
    description: 'We maintain high standards for listings to ensure exceptional care for your animals.',
  },
];

const stats = [
  { value: '500+', label: 'Verified Hosts' },
  { value: '10,000+', label: 'Happy Guests' },
  { value: '50', label: 'States Covered' },
  { value: '4.9', label: 'Average Rating' },
];

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#team') {
      const teamSection = document.getElementById('team');
      if (teamSection) {
        setTimeout(() => {
          teamSection.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-farmhouse-charcoal" data-testid="about-page">
      {/* Hero */}
      <section className="pt-16 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-4">
            Our Story
          </p>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-farmhouse-cream mb-6">
            About Barns & Pastures
          </h1>
          <p className="text-xl text-farmhouse-muted max-w-2xl mx-auto leading-relaxed">
            We're on a mission to make finding quality boarding for your horses and livestock 
            as easy as booking a vacation rental. Built by equestrians, for equestrians.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-farmhouse-paper">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=600&h=400&fit=crop"
                alt="Horse in pasture"
                className="rounded-md shadow-warm-lg"
              />
            </div>
            <div>
              <h2 className="font-serif text-3xl font-semibold text-farmhouse-cream mb-6">
                Born from a Real Need
              </h2>
              <div className="space-y-4 text-farmhouse-muted leading-relaxed">
                <p>
                  As horse owners ourselves, we know the anxiety of finding safe, reliable 
                  boarding—whether for a cross-country move, a short layover, or long-term care. 
                  The process was always fragmented, stressful, and uncertain.
                </p>
                <p>
                  In 2024, we set out to change that. We built Barns & Pastures to create a 
                  trusted marketplace where horse owners can easily find verified hosts, 
                  communicate directly, and book with confidence.
                </p>
                <p>
                  Today, we're proud to serve thousands of horse owners and hosts across 
                  all 50 states, building a community based on trust, quality, and a 
                  shared love for animals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-farmhouse-charcoal">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-serif text-4xl md:text-5xl font-bold text-farmhouse-saddle mb-2">
                  {stat.value}
                </p>
                <p className="text-farmhouse-muted">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-farmhouse-paper">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-3">
              What We Stand For
            </p>
            <h2 className="font-serif text-4xl font-semibold text-farmhouse-cream">
              Our Values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div 
                key={value.title} 
                className="text-center p-6 bg-farmhouse-charcoal border border-farmhouse-subtle rounded-md"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-farmhouse-saddle/10 flex items-center justify-center">
                  <value.icon className="w-7 h-7 text-farmhouse-saddle" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-farmhouse-cream mb-2">
                  {value.title}
                </h3>
                <p className="text-farmhouse-muted text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="py-20 bg-farmhouse-charcoal">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-3">
              The People Behind
            </p>
            <h2 className="font-serif text-4xl font-semibold text-farmhouse-cream">
              Meet Our Team
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div 
                key={member.name} 
                className="text-center p-6 bg-farmhouse-paper border border-farmhouse-subtle rounded-md"
                data-testid={`team-member-${member.initials}`}
              >
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-farmhouse-saddle/20 border-2 border-farmhouse-saddle flex items-center justify-center">
                  <span className="text-2xl font-serif font-bold text-farmhouse-saddle">
                    {member.initials}
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-1">
                  {member.name}
                </h3>
                <p className="text-farmhouse-saddle text-sm mb-3">{member.role}</p>
                <p className="text-farmhouse-muted text-sm leading-relaxed">
                  {member.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-farmhouse-paper">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-6">
            Join Our Community
          </h2>
          <p className="text-lg text-farmhouse-muted mb-8">
            Be part of a trusted network of horse lovers and hosts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button className="btn-primary px-8">
                Explore Listings
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-secondary px-8">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
